import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ChildrenProp,
  type DataFieldValue,
  usePubSub,
  useDebug,
} from "@huub-web/ui";
import {
  type BaseDataFieldResult,
  type DataFieldUpdateResult,
  type TemplateCollection,
  TemplatesProviderContext,
  type TemplatesProviderState,
  type UIContextSlice,
  type UIContextSliceInput,
} from "@huub-web/templating";
import {
  useAPI,
  useDataFieldAPI,
  useWebSocket,
  useWebSocketListener,
  WebSocketChannel,
  type UIContextPageActionsPayload,
  type UIContextPayload,
  type WebSocketTemplatesMessage,
} from "@huub-web/backend";
import {
  isWindowDefined,
  flattenObject,
  type LogColorType,
} from "@huub-web/utils";

import { useNavigate, useAppSettings } from "../../hooks";

export type TemplatesProviderProps = ChildrenProp & {
  lockPrompts?: boolean;
  lockUI?: boolean;
};

type DebugLogFn = (
  color: LogColorType,
  realm: string | string[],
  ...args: unknown[]
) => void;

/**
 * Computes and logs a structural diff between two UIContextPayloads.
 * Only invoked in development when the visual-debug flag is on.
 */
const logUIContextDiff = ({
  prev,
  next,
  slices,
  sliceIDs,
  context,
  log,
}: {
  prev: UIContextPayload;
  next: UIContextPayload;
  slices: UIContextPayload["slices"];
  sliceIDs: string[];
  context: string[];
  log: DebugLogFn;
}) => {
  const diff: Record<string, unknown> = {};

  if (JSON.stringify(prev.context) !== JSON.stringify(context)) {
    diff.context = { prev: prev.context, next: context };
  }
  if (prev.chatOpen !== next.chatOpen) {
    diff.chatOpen = { prev: prev.chatOpen, next: next.chatOpen };
  }

  const prevSlices = prev.slices ?? {};
  const allIds = new Set([...Object.keys(prevSlices), ...sliceIDs]);
  const slicesDiff: Record<string, unknown> = {};

  for (const id of allIds) {
    const ps = prevSlices[id];
    const ns = slices[id];

    if (!ps) {
      slicesDiff[id] = { status: "added", value: ns };
    } else if (!ns) {
      slicesDiff[id] = { status: "removed", value: ps };
    } else if (JSON.stringify(ps) !== JSON.stringify(ns)) {
      const keyDiff: Record<string, unknown> = {};
      const topKeys = new Set([
        ...Object.keys(ps as Record<string, unknown>),
        ...Object.keys(ns as Record<string, unknown>),
      ]);

      for (const key of topKeys) {
        const pv = (ps as Record<string, unknown>)[key];
        const nv = (ns as Record<string, unknown>)[key];
        if (JSON.stringify(pv) === JSON.stringify(nv)) continue;

        const pvPresent = pv !== undefined && pv !== null;
        const nvPresent = nv !== undefined && nv !== null;

        if (!pvPresent && nvPresent) {
          keyDiff[key] = { status: "added", value: nv };
        } else if (pvPresent && !nvPresent) {
          keyDiff[key] = { status: "removed", value: pv };
        } else if (
          typeof pv === "object" &&
          typeof nv === "object" &&
          pvPresent &&
          nvPresent
        ) {
          const prevFlat = flattenObject(pv as Record<string, unknown>, key);
          const nextFlat = flattenObject(nv as Record<string, unknown>, key);
          const leafKeys = new Set([
            ...Object.keys(prevFlat),
            ...Object.keys(nextFlat),
          ]);
          for (const lk of leafKeys) {
            const lpv = prevFlat[lk];
            const lnv = nextFlat[lk];
            if (JSON.stringify(lpv) !== JSON.stringify(lnv)) {
              keyDiff[lk] = { prev: lpv, next: lnv };
            }
          }
        } else {
          keyDiff[key] = { prev: pv, next: nv };
        }
      }

      if (Object.keys(keyDiff).length > 0) {
        slicesDiff[id] = keyDiff;
      }
    }
  }

  if (Object.keys(slicesDiff).length > 0) {
    diff.slices = slicesDiff;
  }

  if (Object.keys(diff).length === 0) {
    log(
      "brown",
      "TemplatesProvider:ui-context",
      "sendUnifiedUIContext (no structural changes)",
    );
  } else {
    log("brown", "TemplatesProvider:ui-context", "DIFF", diff);
  }
};

/**
 * A controller for updating API templating data.
 */
export const TemplatesProvider: FC<TemplatesProviderProps> = ({
  lockPrompts = false,
  lockUI = false,
  children,
}) => {
  const { updateValue } = useDataFieldAPI();
  const { sendMessage, isConnected } = useWebSocket();
  const { $get, $delete, $post, $put } = useAPI();
  const { isVisualDebugEnabled } = useAppSettings();
  const pubSub = usePubSub();
  const { t } = useTranslation();
  const { debug, log } = useDebug();
  const debugRef = useRef(debug);
  debugRef.current = debug;
  const { navigate } = useNavigate();

  const handleFetchTemplates: TemplatesProviderState["fetchTemplates"] = async (
    endpoint: string,
  ) => {
    const response = await $get(endpoint);
    const data = response.data as TemplateCollection;

    return data;
  };

  // Documents
  // ---------

  const handleDocumentUpload: TemplatesProviderState["uploadDocument"] = async (
    endpoint: string,
    file: File,
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await $post(endpoint, formData);
      const data = response.data as BaseDataFieldResult;

      if (data.success === false) {
        pubSub.publish("snackbar", {
          variant: "error",
          body: t("vite.fileUpload.uploadError"),
        });
      }

      return data;
    } catch (error) {
      console.error(error);

      pubSub.publish("snackbar", {
        variant: "error",
        body: t("vite.fileUpload.uploadError"),
      });

      return {
        success: false,
        error: "Failed to upload document",
      };
    }
  };

  const handleDocumentDelete: TemplatesProviderState["deleteDocument"] = async (
    endpoint: string,
    id: string,
  ) => {
    try {
      const response = await $delete(endpoint, { id });
      const data = response.data as BaseDataFieldResult;

      if (data.success === false) {
        pubSub.publish("snackbar", {
          variant: "error",
          body: t("vite.fileUpload.deleteError"),
        });
      }

      return data;
    } catch (error) {
      console.error(error);

      pubSub.publish("snackbar", {
        variant: "error",
        body: t("vite.fileUpload.deleteError"),
      });
    }

    return {
      success: false,
      error: "Failed to delete document",
    };
  };

  const handleDocumentDownload: TemplatesProviderState["downloadDocument"] =
    async (endpoint: string) => {
      log("brown", "DocumentsUploadTemplate", "handleDownload", endpoint);

      if (!endpoint) {
        throw new Error(
          "[DocumentsUploadTemplate] downloadEndpoint is required",
        );
      }

      try {
        // Make GET request to retrieve the file as binary data
        const response = await $get(endpoint, { responseType: "blob" });

        if (response?.data) {
          if (!isWindowDefined()) {
            return;
          }

          // console.log("[DocumentsUploadTemplate] response", response);
          // console.log(
          //   "[DocumentsUploadTemplate] headers type:",
          //   typeof response.headers,
          // );
          // console.log("[DocumentsUploadTemplate] headers:", response.headers);
          // console.log("[DocumentsUploadTemplate] headers entries:", [
          //   ...response.headers.entries(),
          // ]);

          // Extract filename from Content-Disposition header if available
          const contentDisposition =
            response.headers.get("content-disposition") ||
            response.headers.get("Content-Disposition") ||
            "";

          // console.log(
          //   "[DocumentsUploadTemplate] contentDisposition:",
          //   contentDisposition,
          // );

          let filename = "download";

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
            );
            if (filenameMatch?.[1]) {
              filename = filenameMatch[1].replace(/['"]/g, "");
            }
          } else {
            // Fallback: try to extract filename from URL or use a sensible default
            try {
              const urlPath = new URL(endpoint, window.location.origin)
                .pathname;
              const urlFilename = urlPath.split("/").pop();
              if (urlFilename?.includes(".")) {
                filename = urlFilename;
              } else {
                // Use a timestamp-based filename as fallback
                filename = `document_${Date.now()}.pdf`;
              }
            } catch {
              filename = `document_${Date.now()}.pdf`;
            }
          }

          // Create a blob URL from the response data
          const blobUrl = URL.createObjectURL(response.data);

          // Create a temporary anchor element to trigger download
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the blob URL after a short delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } else {
          console.error("[DocumentsUploadTemplate] No file data in response");
        }
      } catch (error) {
        console.error(
          "[DocumentsUploadTemplate] Error viewing document:",
          error,
        );
      }
    };

  // Data field values
  // -----------------

  const [dataFieldValues, setDataFieldValues] = useState<
    TemplatesProviderState["dataFieldValues"]
  >({});

  // Keep a ref in sync so stale closures (e.g. debounced callbacks) can
  // always read the latest registered field values.
  const dataFieldValuesRef = useRef(dataFieldValues);
  dataFieldValuesRef.current = dataFieldValues;

  const handleUpdateDataFieldValue: TemplatesProviderState["updateDataFieldValue"] =
    async (
      endpoint: string,
      value: DataFieldValue,
      linkedFields?: string[],
    ) => {
      log(
        "brown",
        "TemplatesProvider",
        "updateDataFieldValue",
        endpoint,
        value,
      );

      let linkedValues: Record<string, DataFieldValue> | undefined;
      if (linkedFields?.length) {
        linkedValues = {};
        for (const fieldID of linkedFields) {
          const field = dataFieldValuesRef.current[fieldID];
          if (field?.value !== undefined) {
            linkedValues[fieldID] = field.value;
          }
        }
      }

      const response = await updateValue(endpoint, value, linkedValues);
      const data = response.data as DataFieldUpdateResult;

      return data;
    };

  const handleRegisterDataFieldValue: TemplatesProviderState["registerDataFieldValue"] =
    (id: string, value: DataFieldValue, label?: string) => {
      setDataFieldValues((prev) => ({
        ...prev,
        [id]: { value, label },
      }));
    };

  // Template socket update
  // ----------------------

  useWebSocketListener<WebSocketTemplatesMessage<TemplateCollection>>(
    WebSocketChannel.SOCKET_TEMPLATES,
    (message) => {
      log("brown", "TemplatesProvider", "Template socket update", message);

      pubSub.publish("socketTemplatesUpdate", {
        templatesID: message.data.template_id,
        templates: message.data.data?.templates ?? [],
      });
    },
  );

  // UI context registry (unified reporting)
  // ----------------------------------------

  const uiContextRegistryRef = useRef<Map<string, UIContextSlice>>(new Map());
  const pageActionsRef = useRef<UIContextPageActionsPayload | null>(null);
  const navigationContextRef = useRef<string[]>([]);
  const chatOpenRef = useRef(false);
  // Destructive page actions the user has explicitly confirmed out-of-band
  // (SMA-1539). Sent as `confirmedActions` so the backend's trigger_page_action
  // tool can authorize an action the user genuinely confirmed — never the LLM.
  const confirmedActionsRef = useRef<Set<string>>(new Set());
  const [uiContextVersion, setUIContextVersion] = useState(0);
  const uiContextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUnifiedPayloadRef = useRef<UIContextPayload | null>(null);
  const wasWsConnectedRef = useRef(false);

  const handleRegisterUIContext = useCallback(
    (id: string, slice: UIContextSliceInput) => {
      const full = { id, ...slice } as UIContextSlice;
      const existing = uiContextRegistryRef.current.get(id);
      if (existing && JSON.stringify(existing) === JSON.stringify(full)) {
        return;
      }
      uiContextRegistryRef.current.set(id, full);
      setUIContextVersion((v) => v + 1);
    },
    [],
  );

  const handleDeregisterUIContext = useCallback(
    (id: string) => {
      if (uiContextRegistryRef.current.delete(id)) {
        log("brown", "TemplatesProvider:ui-context", "deregisterUIContext", id);
        setUIContextVersion((v) => v + 1);
      }
    },
    [log],
  );

  const handleSetPageActions = useCallback(
    (payload: UIContextPageActionsPayload) => {
      const current = pageActionsRef.current;
      if (current && JSON.stringify(current) === JSON.stringify(payload)) {
        return;
      }
      log("brown", "TemplatesProvider:ui-context", "setPageActions", payload);
      pageActionsRef.current = payload;
      setUIContextVersion((v) => v + 1);
    },
    [log],
  );

  const handleClearPageActions = useCallback(() => {
    // Confirmations authorize a specific destructive page action. Once the
    // actions they belong to are gone, the confirmation is stale and must not
    // linger to authorize a future trigger_page_action (SMA-1539).
    const hadConfirmed = confirmedActionsRef.current.size > 0;
    if (hadConfirmed) {
      confirmedActionsRef.current.clear();
    }
    if (pageActionsRef.current === null) {
      if (hadConfirmed) {
        setUIContextVersion((v) => v + 1);
      }
      return;
    }
    log("brown", "TemplatesProvider:ui-context", "clearPageActions");
    pageActionsRef.current = null;
    setUIContextVersion((v) => v + 1);
  }, [log]);

  const handleSetNavigationContext = useCallback(
    (context: string[]) => {
      if (
        context.length === navigationContextRef.current.length &&
        context.every((v, i) => v === navigationContextRef.current[i])
      )
        return;
      log(
        "brown",
        "TemplatesProvider:ui-context",
        "setNavigationContext",
        context,
      );
      // Clear form slices from the registry on navigation — the old form
      // components will unmount shortly, but there's a brief window where
      // stale formContext would be sent to the backend. Clearing eagerly
      // ensures the first message after navigation has only fresh data.
      // Collect IDs first to avoid skipping entries deleted mid-iteration.
      const formSliceIds = Array.from(uiContextRegistryRef.current)
        .filter(([, slice]) => "formType" in slice)
        .map(([id]) => id);
      for (const id of formSliceIds) {
        uiContextRegistryRef.current.delete(id);
      }
      // Destructive-action confirmations are scoped to the page the user
      // confirmed them on. Drop them on navigation so a stale confirmation
      // can't keep authorizing trigger_page_action after the user has moved
      // to a different context (SMA-1539).
      confirmedActionsRef.current.clear();
      navigationContextRef.current = context;
      setUIContextVersion((v) => v + 1);
    },
    [log],
  );

  const handleSetChatOpen = useCallback(
    (open: boolean) => {
      if (chatOpenRef.current === open) return;
      log("brown", "TemplatesProvider:ui-context", "setChatOpen", open);
      chatOpenRef.current = open;
      setUIContextVersion((v) => v + 1);
    },
    [log],
  );

  const sendUnifiedUIContext = useCallback(() => {
    const slices: UIContextPayload["slices"] = {};
    for (const [id, { id: _id, ...rest }] of uiContextRegistryRef.current) {
      slices[id] = rest;
    }
    const context = navigationContextRef.current;
    const pageActions = pageActionsRef.current;
    const confirmedActions = Array.from(confirmedActionsRef.current);
    const sliceIDs = Object.keys(slices);

    const isEmpty =
      sliceIDs.length === 0 &&
      context.length === 0 &&
      !pageActions &&
      confirmedActions.length === 0 &&
      !chatOpenRef.current;

    if (isEmpty && !lastUnifiedPayloadRef.current) {
      log("brown", "TemplatesProvider:ui-context", "Skipped (no data yet)");
      return;
    }

    const payload: UIContextPayload = {
      context,
      chatOpen: chatOpenRef.current,
      ...(pageActions ? { pageActions } : {}),
      ...(confirmedActions.length > 0 ? { confirmedActions } : {}),
      slices,
    };

    if (import.meta.env.DEV) {
      const prev = lastUnifiedPayloadRef.current;
      if (debugRef.current && prev) {
        logUIContextDiff({
          prev,
          next: payload,
          slices,
          sliceIDs: sliceIDs,
          context,
          log,
        });
      } else if (debugRef.current) {
        log("brown", "TemplatesProvider:ui-context", "First send");
      }
    }

    lastUnifiedPayloadRef.current = payload;

    // -- DEBUG: summary log --
    log("brown", "TemplatesProvider:ui-context", "sendUnifiedUIContext", {
      context: payload.context,
      chatOpen: payload.chatOpen,
      pageActions: pageActions
        ? {
            actions: pageActions.actions?.length ?? 0,
            content: pageActions.content?.length ?? 0,
          }
        : null,
      sliceCount: sliceIDs.length,
      sliceIDs,
      confirmedActions: confirmedActions.length,
    });

    sendMessage(WebSocketChannel.UI_CONTEXT, payload);
  }, [sendMessage, log]);

  const handleConfirmAction = useCallback(
    (actionName: string) => {
      const name = actionName?.trim();
      if (!name || confirmedActionsRef.current.has(name)) return;
      log("brown", "TemplatesProvider:ui-context", "confirmAction", name);
      confirmedActionsRef.current.add(name);
      // Flush the confirmation immediately rather than waiting for the 300ms
      // debounce. The backend may authorize trigger_page_action from the very
      // next message, so it must already see the freshly confirmed label
      // (SMA-1539). Cancel any pending debounced send first to avoid a
      // duplicate payload.
      if (uiContextTimerRef.current) {
        clearTimeout(uiContextTimerRef.current);
        uiContextTimerRef.current = null;
      }
      sendUnifiedUIContext();
    },
    [log, sendUnifiedUIContext],
  );

  useEffect(() => {
    if (uiContextTimerRef.current) {
      clearTimeout(uiContextTimerRef.current);
    }
    uiContextTimerRef.current = setTimeout(() => {
      sendUnifiedUIContext();
      uiContextTimerRef.current = null;
    }, 300);

    return () => {
      if (uiContextTimerRef.current) {
        clearTimeout(uiContextTimerRef.current);
        uiContextTimerRef.current = null;
      }
    };
  }, [uiContextVersion, sendUnifiedUIContext]);

  useEffect(() => {
    if (
      isConnected &&
      !wasWsConnectedRef.current &&
      lastUnifiedPayloadRef.current
    ) {
      log(
        "brown",
        "TemplatesProvider:ui-context",
        "re-send unified context on WS reconnect",
      );
      sendMessage(WebSocketChannel.UI_CONTEXT, lastUnifiedPayloadRef.current);
    }
    wasWsConnectedRef.current = isConnected;
  }, [isConnected, sendMessage, log]);

  // Navigation
  // ----------

  const handleNavigateToPath: TemplatesProviderState["navigateToPath"] = (
    path: string,
  ) => {
    log("brown", "TemplatesProvider", "navigateToPath", path);

    navigate(path);
  };

  return (
    <TemplatesProviderContext.Provider
      value={{
        lockPrompts,
        lockUI,
        debug: isVisualDebugEnabled,
        fetchTemplates: handleFetchTemplates,

        uploadDocument: handleDocumentUpload,
        deleteDocument: handleDocumentDelete,
        downloadDocument: handleDocumentDownload,

        dataFieldValues,
        updateDataFieldValue: handleUpdateDataFieldValue,
        registerDataFieldValue: handleRegisterDataFieldValue,

        navigateToPath: handleNavigateToPath,
        registerUIContext: handleRegisterUIContext,
        deregisterUIContext: handleDeregisterUIContext,
        setPageActions: handleSetPageActions,
        clearPageActions: handleClearPageActions,
        setNavigationContext: handleSetNavigationContext,
        setChatOpen: handleSetChatOpen,
        confirmAction: handleConfirmAction,

        $post: $post as TemplatesProviderState["$post"],
        $get: $get as TemplatesProviderState["$get"],
        $delete: $delete as TemplatesProviderState["$delete"],
        $put: $put as TemplatesProviderState["$put"],

        env: {
          mapBoxToken: import.meta.env.VITE_MAPBOX_TOKEN,
        },
      }}
    >
      {children}
    </TemplatesProviderContext.Provider>
  );
};
